import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper';
import { Pagination, Navigation } from 'swiper/modules';
import { md, lg, xl, wide, xwide } from '../common';

const videoLoadPromises = new WeakMap();
const VIDEO_LOAD_TIMEOUT_MS = 1500;

function loadVideoSource(videoEl) {
  if (!videoEl) return Promise.resolve();

  if (videoEl.dataset.videoLoaded === 'true') {
    return Promise.resolve();
  }

  const existingPromise = videoLoadPromises.get(videoEl);
  if (existingPromise) {
    return existingPromise;
  }

  const src = (videoEl.dataset.src || '').trim();
  if (!src) {
    videoEl.dataset.videoLoaded = 'true';
    return Promise.resolve();
  }

  if (videoEl.getAttribute('src') !== src) {
    videoEl.setAttribute('src', src);
    videoEl.load();
  }

  if (videoEl.readyState >= 1) {
    videoEl.dataset.videoLoaded = 'true';
    return Promise.resolve();
  }

  const loadPromise = new Promise((resolve) => {
    let isResolved = false;
    let timeoutId = null;

    const onReady = () => {
      if (isResolved) return;
      isResolved = true;

      videoEl.removeEventListener('loadedmetadata', onReady);
      videoEl.removeEventListener('loadeddata', onReady);
      videoEl.removeEventListener('canplay', onReady);
      videoEl.removeEventListener('error', onReady);

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }

      videoEl.dataset.videoLoaded = 'true';
      videoLoadPromises.delete(videoEl);
      resolve();
    };

    videoEl.addEventListener('loadedmetadata', onReady, { once: true });
    videoEl.addEventListener('loadeddata', onReady, { once: true });
    videoEl.addEventListener('canplay', onReady, { once: true });
    videoEl.addEventListener('error', onReady, { once: true });
    timeoutId = window.setTimeout(onReady, VIDEO_LOAD_TIMEOUT_MS);

    if (videoEl.readyState >= 1) {
      onReady();
    }
  });

  videoLoadPromises.set(videoEl, loadPromise);
  return loadPromise;
}

function playVideo(videoEl, { restart = false } = {}) {
  if (!videoEl || videoEl.hidden) return;

  if (restart) {
    videoEl.currentTime = 0;
  }

  const tryPlay = () => {
    if (videoEl.hidden) return;
    const playPromise = videoEl.play();
    if (playPromise?.catch) {
      playPromise.catch(() => null);
    }
  };

  tryPlay();

  if (videoEl.readyState < 2) {
    videoEl.addEventListener('canplay', tryPlay, { once: true });
  }
}

function pauseVideo(videoEl, { reset = false } = {}) {
  if (!videoEl) return;
  videoEl.pause();

  if (reset) {
    videoEl.currentTime = 0;
  }
}

function isCardInActiveSlide(card) {
  const slide = card.closest('.swiper-slide');
  return !slide || slide.classList.contains('swiper-slide-active');
}

function initCardMediaSwitch(card) {
  if (!card || card.dataset.mediaSwitchInitialized === 'true') return null;
  card.dataset.mediaSwitchInitialized = 'true';

  const videos = Array.from(
    card.querySelectorAll('.js-videos-slider-media-video'),
  );
  if (!videos.length) return null;

  let mediaIndex = videos.findIndex((videoEl) => !videoEl.hidden);
  if (mediaIndex === -1) {
    mediaIndex = 0;
  }

  videos.forEach((videoEl, index) => {
    videoEl.hidden = index !== mediaIndex;
  });

  const prevBtn = card.querySelector('.js-videos-slider-media-prev');
  const nextBtn = card.querySelector('.js-videos-slider-media-next');
  const hasSwitch = Boolean(prevBtn && nextBtn && videos.length > 1);

  const syncNavigationState = () => {
    if (!prevBtn || !nextBtn) return;

    const isPrevDisabled = mediaIndex === 0;
    const isNextDisabled = mediaIndex === videos.length - 1;

    prevBtn.classList.toggle('swiper-button-disabled', isPrevDisabled);
    nextBtn.classList.toggle('swiper-button-disabled', isNextDisabled);
    prevBtn.disabled = isPrevDisabled;
    nextBtn.disabled = isNextDisabled;
    prevBtn.setAttribute('aria-disabled', String(isPrevDisabled));
    nextBtn.setAttribute('aria-disabled', String(isNextDisabled));
  };

  const getActiveVideo = () => videos[mediaIndex] || null;

  const preloadActiveVideo = async () => {
    const activeVideo = getActiveVideo();
    if (!activeVideo) return;
    await loadVideoSource(activeVideo);
  };

  const playActiveVideo = async ({ restart = false } = {}) => {
    const activeVideo = getActiveVideo();
    if (!activeVideo) return;

    await loadVideoSource(activeVideo);
    if (!isCardInActiveSlide(card)) return;
    playVideo(activeVideo, { restart });
  };

  const pauseAllVideos = () => {
    videos.forEach((videoEl) => pauseVideo(videoEl));
  };

  const switchMedia = async (nextIndex) => {
    if (
      nextIndex === mediaIndex ||
      nextIndex < 0 ||
      nextIndex >= videos.length
    ) {
      return;
    }

    const currentVideo = getActiveVideo();
    const nextVideo = videos[nextIndex];

    await loadVideoSource(nextVideo);

    if (currentVideo) {
      pauseVideo(currentVideo, { reset: true });
      currentVideo.hidden = true;
    }

    mediaIndex = nextIndex;
    nextVideo.hidden = false;
    syncNavigationState();

    if (isCardInActiveSlide(card)) {
      playVideo(nextVideo, { restart: true });
    }
  };

  syncNavigationState();

  if (hasSwitch) {
    prevBtn.addEventListener('click', () => {
      if (mediaIndex === 0) return;
      switchMedia(mediaIndex - 1);
    });

    nextBtn.addEventListener('click', () => {
      if (mediaIndex >= videos.length - 1) return;
      switchMedia(mediaIndex + 1);
    });
  }

  return {
    card,
    pauseAllVideos,
    preloadActiveVideo,
    playActiveVideo,
  };
}

function initCardLazyLoad(state) {
  const { card } = state;

  if (card.dataset.mediaScrollTriggerInitialized === 'true') return;
  card.dataset.mediaScrollTriggerInitialized = 'true';

  ScrollTrigger.create({
    trigger: card,
    start: 'top bottom+=100%',
    once: true,
    onEnter: async () => {
      await state.preloadActiveVideo();
      state.playActiveVideo({ restart: true });
    },
  });
}

export function initBlockVideosSlider(parent) {
  if (!parent || parent.dataset.videosSliderInitialized === 'true') return;
  parent.dataset.videosSliderInitialized = 'true';

  const cards = Array.from(parent.querySelectorAll('.js-videos-slider-card'));
  const cardStates = cards
    .map((card) => initCardMediaSwitch(card))
    .filter(Boolean);

  cardStates.forEach((state) => {
    initCardLazyLoad(state);
  });

  const syncActiveSlideMedia = () => {
    cardStates.forEach((state) => {
      if (isCardInActiveSlide(state.card)) {
        state.playActiveVideo({ restart: true });
      } else {
        state.pauseAllVideos();
      }
    });
  };

  const swiperEl = parent.querySelector('.block-videos-slider__swiper');
  const nextEl = parent.querySelector(
    '.block-videos-slider__swiper-navigation .swiper-button-next',
  );
  const prevEl = parent.querySelector(
    '.block-videos-slider__swiper-navigation .swiper-button-prev',
  );
  const paginationEl = parent.querySelector('.swiper-pagination');

  if (swiperEl) {
    new Swiper(swiperEl, {
      modules: [Pagination, Navigation],
      slidesPerView: 1,
      spaceBetween: 16,
      watchOverflow: false,
      navigation: {
        prevEl,
        nextEl,
      },
      pagination: {
        el: paginationEl,
        clickable: true,
      },
      on: {
        init: syncActiveSlideMedia,
        slideChange: syncActiveSlideMedia,
        slideChangeTransitionEnd: syncActiveSlideMedia,
      },
      breakpoints: {
        [md]: { spaceBetween: 20 },
        [lg]: { spaceBetween: 20 },
        [xl]: { spaceBetween: 24 },
        [xwide]: { spaceBetween: 32 },
      },
    });

    return;
  }

  syncActiveSlideMedia();
}

export function initAllBlockVideosSliders() {
  const blocks = document.querySelectorAll('.js-block-videos-slider');
  blocks.forEach((block) => initBlockVideosSlider(block));
}
